import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, SubmitErrorHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useSnackbar } from 'notistack';
import { z } from 'zod';

import { routes } from '@app/routes';
import { Form } from '@components/form';
import {
  AssessmentNameCard,
  AssessmentPageHeader,
  AssessmentSummaryCard,
  AssessmentTipsCard,
  DomainOverviewCard,
  useCreateAssessment,
} from '@features/assessment';
import { useFrameworkDetails } from '@features/framework';
import {
  ProfileCompletionModal,
  shouldShowCompletionPopup,
  useProfileNudgeSuppression,
  useProfileSummary,
} from '@features/tenant-profile-hub';
import { useDirection } from '@hooks';
import { FrameworkKey, LanguageCode } from '@libs/constants';
import { Trans, useTrans } from '@libs/i18n';
import { DomainOverviewCard as DomainOverviewCardType, SupportedLanguages } from '@libs/types';
import { ArrowLeftIcon, PlayIcon, ShieldPlusIcon } from '@phosphor-icons/react';

export const NewAssessmentPage: FC = observer(() => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTrans();
  const { isRtl } = useDirection();
  const [isVisible, setIsVisible] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(LanguageCode.EN);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLDivElement>(null);

  // Organization Profile completion nudge: a one-time popup shown when the
  // profile has zero fields filled, and a reduced-accuracy warning if skipped.
  const { data: profileSummary } = useProfileSummary();
  const { popupSuppressed, suppressPopup } = useProfileNudgeSuppression();
  const [showProfileSkippedWarning, setShowProfileSkippedWarning] = useState(false);
  const showProfileModal = profileSummary
    ? shouldShowCompletionPopup(profileSummary.fieldsFilled, popupSuppressed)
    : false;

  const handleProfileModalSkip = () => {
    suppressPopup();
    setShowProfileSkippedWarning(true);
  };

  const handleProfileModalComplete = () => {
    suppressPopup();
    navigate('/tenant-profile');
  };

  const assessmentSchema = z.object({
    assessmentName: z.string().trim().min(1, t('app.assessments.workflow.nameRequired')),
  });

  type AssessmentForm = z.infer<typeof assessmentSchema>;

  // Fetch framework details from API
  const {
    data: frameworkData,
    isLoading,
    isError,
    refetch,
  } = useFrameworkDetails(FrameworkKey.PDPL);

  // Create assessment mutation
  const { mutate: createAssessment, isPending: isCreatingAssessment } = useCreateAssessment();

  // Calculate assessment items and totals from API data
  const { assessmentItems, totalEvidenceDocuments, totalMinEstimatedTime, totalMaxEstimatedTime } =
    useMemo(() => {
      if (!frameworkData) {
        return {
          assessmentItems: [],
          totalEvidenceDocuments: 0,
          totalMinEstimatedTime: 0,
          totalMaxEstimatedTime: 0,
        };
      }

      let totalEvidence = 0;
      let totalMinTime = 0;
      let totalMaxTime = 0;

      const items: DomainOverviewCardType[] = frameworkData.domains
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((domain) => {
          const evidenceCount = domain.documentRequiredCount;

          // Accumulate totals
          totalEvidence += evidenceCount;
          totalMinTime += domain.minEstimatedTime;
          totalMaxTime += domain.maxEstimatedTime;

          return {
            id: domain.id,
            name: domain.name,
            domainKey: domain.domainKey,
            orderIndex: domain.orderIndex,
            weight: domain.weight,
            required: domain.required,
            evidenceCount,
            minEstimatedTime: domain.minEstimatedTime,
            maxEstimatedTime: domain.maxEstimatedTime,
            description: domain.description || '',
          };
        });

      return {
        assessmentItems: items,
        totalEvidenceDocuments: totalEvidence,
        totalMinEstimatedTime: totalMinTime,
        totalMaxEstimatedTime: totalMaxTime,
      };
    }, [frameworkData]);

  const handleLanguageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedLanguage(event.target.value as SupportedLanguages);
  };

  const onSubmit = (data: AssessmentForm) => {
    if (!frameworkData?.framework.id) {
      return;
    }

    createAssessment(
      {
        frameworkId: frameworkData.framework.id,
        title: data.assessmentName,
        language: selectedLanguage,
      },
      {
        onSuccess: (response) => {
          navigate(routes.assessmentDomain(response.assessmentId, response.assessmentDomainId));
        },
      },
    );
  };

  const onError: SubmitErrorHandler<AssessmentForm> = (errors) => {
    if (errors.assessmentName) {
      enqueueSnackbar(errors.assessmentName.message, { variant: 'error' });
      nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const listenToScroll = () => {
      const heightToHideFrom = 155;
      const scrollTop = scrollContainer.scrollTop;

      if (scrollTop > heightToHideFrom) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    scrollContainer.addEventListener('scroll', listenToScroll);
    return () => scrollContainer.removeEventListener('scroll', listenToScroll);
  }, []);

  // Error State Section: Displays error alert with retry button when framework data fails to load
  if (isError) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: 4,
          p: 3,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            gap: 2,
          }}
        >
          {/* Error Alert: Shows error message about framework loading failure */}
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
              <Trans i18nKey="app.assessments.error.failedToLoadFramework" />
            </Typography>
            <Typography variant="body2">
              <Trans i18nKey="app.assessments.error.frameworkLoadError" />
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()}>
            <Trans i18nKey="app.assessments.error.retry" />
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        borderRadius: 4,
        p: 3,
      }}
    >
      <ProfileCompletionModal
        open={Boolean(showProfileModal)}
        onSkip={handleProfileModalSkip}
        onComplete={handleProfileModalComplete}
      />

      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          backgroundColor: '#ffffff',
          borderRadius: 4,
          overflow: 'auto',
          p: 3,
          py: 5,
          pt: 0,
          scrollbarWidth: 'none',
          maxWidth: '1200px',
          margin: 'auto',
          width: '100%',
        }}
      >
        {/* Sticky Header: Assessment page header that appears when scrolling down */}
        {isVisible && <AssessmentPageHeader />}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ my: 3, mt: 5 }}>
            {/* Back Button: Navigation button to return to assessments list */}
            <Button
              startIcon={
                <ArrowLeftIcon size={24} style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }} />
              }
              onClick={() => navigate(routes.assessmentList())}
              variant="outlined"
              sx={{
                borderRadius: 1,
                px: 1.875,
                paddingInlineStart: 1.25,
                py: 1.5,
                backgroundColor: 'transparent',
                color: 'secondary.main',
                border: '1px none',
              }}
            >
              <Typography variant="textMD_medium">
                <Trans i18nKey="assessments.new.back" />
              </Typography>
            </Button>

            {/* Page Title and Description Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="textXL_medium">
                <Trans i18nKey="assessments.new.title" />
              </Typography>
              <Typography variant="textMD_medium" sx={{ color: 'text.tertiary' }}>
                <Trans i18nKey="assessments.new.description" />
              </Typography>
            </Box>

            {showProfileSkippedWarning && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
                onClose={() => setShowProfileSkippedWarning(false)}
              >
                {t('app.orgProfile.nudge.modal.skippedWarning')}
              </Alert>
            )}

            <Divider sx={{ width: '100%', my: 2 }} />

            <Box sx={{ width: '100%', height: 1, backgroundColor: 'grey.100', mt: 1.5 }} />

            {/* Loading State */}
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  py: 8,
                }}
              >
                <CircularProgress size={48} />
              </Box>
            ) : (
              <Form
                schema={assessmentSchema}
                onSubmit={onSubmit}
                onError={onError}
                options={{
                  defaultValues: {
                    assessmentName: '',
                  },
                }}
                className="w-full"
              >
                {({ control, formState: { errors }, trigger }) => (
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, mt: 3 }}>
                    <Box
                      sx={{
                        height: '100%',
                        flex: 2,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      {/* Check if framework exists */}
                      {!frameworkData?.framework.id && (
                        <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                          <Trans i18nKey="app.assessments.error.failedToLoadFramework" />
                        </Alert>
                      )}

                      {/* Assessment Details Card */}
                      <Controller
                        name="assessmentName"
                        control={control}
                        render={({ field }) => (
                          <AssessmentNameCard
                            ref={nameInputRef}
                            assessmentName={field.value}
                            onNameChange={(name) => {
                              field.onChange(name);
                              if (errors.assessmentName) {
                                trigger('assessmentName');
                              }
                            }}
                            error={!!errors.assessmentName}
                          />
                        )}
                      />

                      {/* Language Selection Card */}
                      <Paper
                        elevation={1}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          borderRadius: 2,
                          mt: 3,
                          overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ p: 3, display: "flex", flexDirection: "column" }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              pb: 2,
                            }}
                          >
                            <ShieldPlusIcon color="#858585" size={20} />
                            <Typography variant="textMD_medium" sx={{ ml: 1 }}>
                              <Trans i18nKey="assessments.new.languages.title" />
                            </Typography>
                          </Box>
                          <Typography
                            variant="textSM_medium"
                            sx={{
                              color: 'text.tertiary',
                              mb: 2,
                            }}
                          >
                            <Trans i18nKey="assessments.new.languages.description" />
                          </Typography>
                          <FormControl component="fieldset">
                            <RadioGroup
                              value={selectedLanguage}
                              onChange={handleLanguageChange}
                              sx={{ gap: 1 }}
                            >
                              {frameworkData?.framework.supportedLanguages.map((language) => (
                                <FormControlLabel
                                  key={language.code}
                                  value={language.code}
                                  control={<Radio sx={{ color: 'grey.300' }} size="small" />}
                                  label={
                                    <Typography variant="textSM_medium">
                                      {language.label}
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                              ))}
                            </RadioGroup>
                          </FormControl>
                        </Box>
                      </Paper>

                      {/* Domains List */}
                      <Paper
                        elevation={1}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          borderRadius: 2,
                          mt: 3,
                        }}
                      >
                        <Box sx={{ p: 3 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <ShieldPlusIcon color="#858585" size={20} />
                            <Typography
                              variant="textMD_medium"
                              sx={{ fontWeight: 500, fontSize: '1rem' }}
                            >
                              <Trans i18nKey="assessments.new.list.title" />
                            </Typography>
                          </Box>

                          {assessmentItems.map((item) => (
                            <DomainOverviewCard key={item.id} item={item} />
                          ))}
                        </Box>
                      </Paper>
                    </Box>

                    {/* Right Sidebar */}
                    <Box
                      sx={{
                        height: '100%',
                        flex: 1,
                        maxWidth: '298px',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'sticky',
                        top: 70,
                      }}
                    >
                      <AssessmentSummaryCard
                        evidenceDocumentsCount={totalEvidenceDocuments}
                        minEstimatedTime={totalMinEstimatedTime}
                        maxEstimatedTime={totalMaxEstimatedTime}
                      />
                      <AssessmentTipsCard />
                      <Box
                        sx={{
                          width: '100%',
                          mt: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                        }}
                      >
                        <Button
                          variant="contained"
                          sx={{ borderRadius: 2, py: 1.5, px: 3.75 }}
                          type="submit"
                          disabled={isCreatingAssessment || !frameworkData}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isCreatingAssessment ? (
                              <CircularProgress size={20} sx={{ color: 'white' }} />
                            ) : (
                              <PlayIcon
                                size={24}
                                style={{ transform: isRtl ? 'scaleX(-1)' : 'none' }}
                              />
                            )}
                            <Typography variant="textMD_semiBold" sx={{ color: 'white' }}>
                              <Trans i18nKey="assessments.new.button.startAssessment" />
                            </Typography>
                          </Box>
                        </Button>
                        <Typography
                          variant="textXS_regular"
                          sx={{ color: 'text.tertiary', textAlign: 'center' }}
                        >
                          <Trans i18nKey="assessments.new.button.description" />
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Form>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});
